import React from 'react';
import { Building, User, Stethoscope } from 'lucide-react';

export const RegisterBasicInfo = ({ formData, handleChange, handleGoogleSignUp, isDoctor, isHospital, loading, processingOAuth, setFormData, t }) => (
    <div className="space-y-4">
      {/* Google Sign-Up Option (only for patients) */}
      {formData.role === 'patient' && (
        <>
          {processingOAuth ? (
            <div className="w-full flex items-center justify-center py-3 border border-[hsl(var(--border))] rounded-lg bg-gray-50">
              <svg className="animate-spin h-5 w-5 mr-3 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-gray-600">Processing Google Sign-Up...</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={handleGoogleSignUp}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 py-2.5 px-4 border border-[hsl(var(--border))] rounded-lg font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors"
              data-testid="google-signup-button"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Sign up with Google</span>
            </button>
          )}
          <div className="flex items-center my-4">
            <div className="flex-1 border-t border-[hsl(var(--border))]"></div>
            <span className="px-4 text-sm text-[hsl(var(--muted-foreground))]">or fill the form below</span>
            <div className="flex-1 border-t border-[hsl(var(--border))]"></div>
          </div>
        </>
      )}
      
      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="role">
          {t('register.roleLabel')}
        </label>
        <div className="grid grid-cols-3 gap-2">
          {[
            { value: 'patient', icon: User, label: t('register.rolePatient') },
            { value: 'doctor', icon: Stethoscope, label: t('register.roleDoctor') },
            { value: 'hospital', icon: Building, label: t('register.roleHospital') }
          ].map(({ value, icon: Icon, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setFormData({ ...formData, role: value })}
              className={`p-3 rounded-lg border-2 transition-all ${
                formData.role === value 
                  ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Icon className={`w-5 h-5 mx-auto mb-1 ${formData.role === value ? 'text-[hsl(var(--primary))]' : 'text-gray-400'}`} />
              <span className={`text-xs font-medium ${formData.role === value ? 'text-[hsl(var(--primary))]' : 'text-gray-600'}`}>
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {isHospital ? (
        <div>
          <label className="block text-sm font-medium mb-1.5">Hospital Name *</label>
          <input
            name="hospital_name"
            type="text"
            required
            value={formData.hospital_name}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
            placeholder="Apollo Hospital"
            data-testid="register-hospital-name"
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="full_name">
            {t('register.fullNameLabel')} {isDoctor && '(with Dr. prefix)'}
          </label>
          <input
            id="full_name"
            name="full_name"
            type="text"
            required
            value={formData.full_name}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
            placeholder={isDoctor ? "Dr. John Smith" : "John Smith"}
            data-testid="register-name-input"
          />
        </div>
      )}

      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="email">
          {t('register.emailLabel')}
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          value={formData.email}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          data-testid="register-email-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="password">
          {t('register.passwordLabel')}
        </label>
        <input
          id="password"
          name="password"
          type="password"
          required
          value={formData.password}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          data-testid="register-password-input"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-1.5" htmlFor="phone">
          {t('register.phoneLabel')} {(isHospital || isDoctor) && '*'}
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          required={isHospital || isDoctor}
          value={formData.phone}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          placeholder="+91 98765 43210"
          data-testid="register-phone-input"
        />
      </div>

      {!isHospital && !isDoctor && (
        <div>
          <label className="block text-sm font-medium mb-1.5" htmlFor="country">
            {t('register.countryLabel')}
          </label>
          <input
            id="country"
            name="country"
            type="text"
            value={formData.country}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
            data-testid="register-country-input"
            placeholder={t('register.countryPlaceholder')}
          />
        </div>
      )}

      {isHospital && (
        <div>
          <label className="block text-sm font-medium mb-1.5">Hospital Type</label>
          <select
            name="hospital_type"
            value={formData.hospital_type}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-[hsl(var(--border))] rounded-lg focus:outline-none focus:ring-2 focus:ring-[hsl(var(--ring))]"
          >
            <option value="multi-specialty">Multi-Specialty Hospital</option>
            <option value="super-specialty">Super-Specialty Hospital</option>
            <option value="single-specialty">Single-Specialty Hospital</option>
            <option value="clinic">Clinic</option>
            <option value="diagnostic-center">Diagnostic Center</option>
          </select>
        </div>
      )}
    </div>
);
